import { useEffect, useMemo, useState } from "react";

import { SelectorDeComuna } from "@/components/informes/SelectorDeComuna";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { numero, pesos } from "@/lib/formatoDeMercado";
import { numeroEscrito } from "@/lib/numeroEscrito";
import type { CorreccionDeComparable, ReferenciaDelInforme } from "@/lib/referencias";

interface ModalDeCorreccionProps {
  referencia: ReferenciaDelInforme | null;
  abierto: boolean;
  onCerrar: () => void;
  onGuardar: (correccion: CorreccionDeComparable) => void;
  guardando?: boolean;
}

/** Lo que el formulario tiene escrito: todo texto, incluso las cifras. */
interface Formulario {
  titulo: string;
  descripcion: string;
  comunaCodigo: string | null;
  referencia: string;
  fuente: string;
  superficieHa: string;
  valorTotal: string;
}

/**
 * Corregir los datos de un comparable.
 *
 * POR QUÉ EXISTE: el catálogo de mercado se arma leyendo portales y archivos
 * del SII, y de ahí salen datos mal leídos —una superficie con un dígito de
 * más, una comuna que el aviso escribió raro— y datos que no vienen: una
 * transferencia del CBR no tiene descripción, porque no es un aviso. Alguien
 * tiene que poder arreglarlos, y ese alguien es quien está tasando.
 *
 * LO QUE SE CORRIGE ACÁ NO ES SOLO DE ESTE INFORME. La corrección queda
 * guardada contra la ficha del catálogo, así que el siguiente tasador que la
 * mire la ve corregida. Es la diferencia con validar, descartar y homologar,
 * que son juicios sobre ESTA tasación. Los informes que ya enlazaron esa ficha
 * no se tocan: sus copias siguen diciendo lo que decían.
 *
 * LOS VALORES UNITARIOS NO SE EDITAN, SE CALCULAN
 *
 * Salen de dividir el valor total por la superficie. Ofrecerlos como campos
 * dejaría que las tres cifras se contradigan —alguien corrige la superficie y
 * el UF/ha sigue diciendo lo de antes— en un informe pericial, y ese UF/ha es
 * el que alimenta el gráfico, la media del panel y la homologación. Se muestran
 * en gris, moviéndose con lo que se escribe, que es lo que hace evidente el
 * error de tipeo antes de guardar.
 */
export function ModalDeCorreccion({
  referencia,
  abierto,
  onCerrar,
  onGuardar,
  guardando = false,
}: ModalDeCorreccionProps) {
  const [formulario, setFormulario] = useState<Formulario>(() => vacio());

  // Se rehidrata al ABRIR y no en cada render: mientras el modal está abierto,
  // lo que manda es lo que la persona escribió. Sin la guarda de `abierto`, una
  // respuesta del servidor que refresque la lista pisaría el formulario a medio
  // llenar.
  useEffect(() => {
    if (abierto && referencia) setFormulario(desdeLaReferencia(referencia));
  }, [abierto, referencia]);

  const superficieM2 = useMemo(() => {
    const ha = numeroEscrito(formulario.superficieHa);

    return ha === null ? null : ha * 10_000;
  }, [formulario.superficieHa]);

  const valorTotal = useMemo(
    () => numeroEscrito(formulario.valorTotal),
    [formulario.valorTotal],
  );

  const moneda = referencia?.precio_moneda ?? null;

  // Los dos unitarios, con la misma cuenta que hace la ficha. El de UF necesita
  // la tasa que la copia guarda desde que se enlazó, así que no depende del
  // catálogo: sin ella la cifra queda vacía en vez de aproximada.
  const ufValorClp = aNumero(referencia?.uf_valor_clp ?? null);
  const superficieHaEfectiva = superficieM2 === null ? null : superficieM2 / 10_000;

  const unitarioPesos =
    moneda === "CLP" && valorTotal !== null && superficieHaEfectiva
      ? valorTotal / superficieHaEfectiva
      : null;

  const precioUf =
    moneda === "UF"
      ? valorTotal
      : moneda === "CLP" && valorTotal !== null && ufValorClp
        ? valorTotal / ufValorClp
        : null;

  const unitarioUf =
    precioUf !== null && superficieHaEfectiva ? precioUf / superficieHaEfectiva : null;

  const campo = <C extends keyof Formulario>(clave: C, valor: Formulario[C]) =>
    setFormulario((actual) => ({ ...actual, [clave]: valor }));

  function guardar() {
    onGuardar({
      titulo: aTextoONulo(formulario.titulo),
      descripcion: aTextoONulo(formulario.descripcion),
      comuna_codigo: formulario.comunaCodigo,
      referencia: aTextoONulo(formulario.referencia),
      fuente: aTextoONulo(formulario.fuente),
      superficie_terreno_m2: superficieM2,
      precio_monto: valorTotal,
      precio_moneda: moneda,
    });
  }

  // Se avisa cuando hay algo escrito que no se pudo leer como número. Sin esto
  // el campo se vaciaría al guardar y la persona vería desaparecer un dato que
  // ella misma acaba de escribir — mismo trato que las fechas a medio teclear.
  const superficieIlegible =
    formulario.superficieHa.trim() !== "" && superficieM2 === null;
  const valorIlegible = formulario.valorTotal.trim() !== "" && valorTotal === null;

  return (
    <Dialog open={abierto} onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar propiedad comparable</DialogTitle>
          <DialogDescription>
            La corrección queda guardada sobre la ficha del catálogo: la verá quien
            use este comparable en otra tasación. Los informes que ya lo tienen no
            cambian.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="correccion-titulo">Propiedad</Label>
            <Input
              id="correccion-titulo"
              value={formulario.titulo}
              onChange={(e) => campo("titulo", e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="correccion-comuna">Comuna</Label>
              <SelectorDeComuna
                id="correccion-comuna"
                valor={formulario.comunaCodigo}
                onValorChange={(codigo) => campo("comunaCodigo", codigo)}
                nombreCongelado={referencia?.comuna}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="correccion-superficie">Superficie (ha)</Label>
              <Input
                id="correccion-superficie"
                inputMode="decimal"
                value={formulario.superficieHa}
                onChange={(e) => campo("superficieHa", e.target.value)}
                aria-invalid={superficieIlegible}
              />
              {superficieIlegible && (
                <p className="text-danger-soft-foreground text-caption">
                  No se entiende ese número. Escríbelo como 4,68.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="correccion-valor">
                Valor total {moneda === "UF" ? "(UF)" : "($)"}
              </Label>
              <Input
                id="correccion-valor"
                inputMode="decimal"
                value={formulario.valorTotal}
                onChange={(e) => campo("valorTotal", e.target.value)}
                aria-invalid={valorIlegible}
              />
              {valorIlegible ? (
                <p className="text-danger-soft-foreground text-caption">
                  No se entiende ese número. Escríbelo como 150.000.000.
                </p>
              ) : (
                // LA LECTURA A LA VISTA. Un punto de miles interpretado como
                // decimal convierte 150.000.000 en 150 sin que nada se vea mal,
                // así que el sistema dice en voz alta cómo entendió lo escrito
                // en vez de resolver la ambigüedad a espaldas de la persona.
                valorTotal !== null && (
                  <p className="text-ink-hint text-caption">
                    Se guardará {moneda === "UF" ? numero(valorTotal, 2) : pesos(valorTotal)}
                  </p>
                )
              )}
            </div>

            <Calculado rotulo="Valor unit. ($ x ha)" valor={pesos(unitarioPesos)} />
            <Calculado rotulo="Valor unit. (UF x ha)" valor={numero(unitarioUf, 2)} />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="correccion-descripcion">Observaciones y fuente</Label>
            <Textarea
              id="correccion-descripcion"
              rows={4}
              value={formulario.descripcion}
              onChange={(e) => campo("descripcion", e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="correccion-referencia">
                {referencia?.clase === "transaccion" ? "Rol SII" : "Referencia del portal"}
              </Label>
              <Input
                id="correccion-referencia"
                value={formulario.referencia}
                onChange={(e) => campo("referencia", e.target.value)}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="correccion-fuente">Fuente</Label>
              <Input
                id="correccion-fuente"
                value={formulario.fuente}
                onChange={(e) => campo("fuente", e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            onClick={guardar}
            // Se bloquea solo por lo que NO se puede guardar sin mentir: un
            // número a medio escribir se convertiría en vacío al enviarse.
            disabled={guardando || superficieIlegible || valorIlegible}
          >
            {guardando && <Spinner className="size-4" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Una cifra que el sistema calcula y la persona no escribe.
 *
 * Se muestra —y no se esconde— porque entra al análisis: sin ella no habría
 * cómo notar que la superficie recién corregida dejó el valor unitario en algo
 * absurdo, que es justamente el error que el modal existe para atajar.
 */
function Calculado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="grid gap-1.5">
      <Label aria-hidden>{rotulo}</Label>
      <output
        aria-label={rotulo}
        className="border-border bg-muted/40 text-ink-muted flex h-11 items-center rounded-md border-[1.5px] px-3.5 font-mono text-sm tabular-nums"
      >
        {valor}
      </output>
    </div>
  );
}

function vacio(): Formulario {
  return {
    titulo: "",
    descripcion: "",
    comunaCodigo: null,
    referencia: "",
    fuente: "",
    superficieHa: "",
    valorTotal: "",
  };
}

/**
 * El formulario partiendo de lo que la ficha dice HOY.
 *
 * Las cifras se escriben SIN separadores de miles y con coma decimal: con
 * puntos, el propio formulario devolvería un texto que hay que volver a
 * interpretar, y bastaría no tocar el campo para que el número diera un viaje
 * de ida y vuelta por la heurística. Lo que se muestra formateado es la lectura
 * de abajo, que es donde el formato ayuda sin poder romper nada.
 */
function desdeLaReferencia(referencia: ReferenciaDelInforme): Formulario {
  const m2 = aNumero(referencia.superficie_terreno_m2);
  const monto = aNumero(referencia.precio_monto);

  return {
    titulo: referencia.titulo ?? "",
    descripcion: referencia.descripcion ?? "",
    comunaCodigo: referencia.comuna_codigo,
    referencia: referencia.referencia ?? "",
    fuente: referencia.fuente ?? "",
    superficieHa: m2 === null ? "" : conComa(m2 / 10_000),
    valorTotal: monto === null ? "" : conComa(monto),
  };
}

/** Sin separador de miles y con coma decimal, que es como se escribe acá. */
function conComa(valor: number): string {
  return String(Number(valor.toFixed(4))).replace(".", ",");
}

/**
 * Un campo de texto vacío es `null` y no `""`.
 *
 * La diferencia importa: `null` en la base significa "este dato no existe" —una
 * transacción del CBR sin descripción— y la ficha OMITE el bloque. Una cadena
 * vacía diría que existe y está en blanco.
 */
function aTextoONulo(valor: string): string | null {
  const limpio = valor.trim();

  return limpio === "" ? null : limpio;
}

/**
 * Los `decimal:*` de PHP llegan como STRING, y `null` tiene que seguir siendo
 * `null`: `Number(null)` es 0, y un cero acá diría que la ficha mide o vale
 * cero cuando lo que pasa es que el dato no está.
 */
function aNumero(valor: string | null): number | null {
  if (valor === null) return null;
  const n = Number(valor);

  return Number.isFinite(n) ? n : null;
}
